import { DisplayOccasionWithoutIcon } from '@/components/DisplayOccasionWithIcon/DisplayOccasionWithIcon';
import Header from '@/components/Header/Header';
import Link from '@/components/Link/Link';
import Table from '@/components/Tables/Table/Table';
import { showToast } from '@/components/Toast/Toast';
import Card from '@/components/Card/Card';
import RadioGroup from '@/components/RadioGroup/RadioGroup';
import { ToastTypes } from '@/components/Toast/Toast.types';
import {
  filterBooleans,
  filterNumbers,
  filterPromotionOccasions,
} from '@/services/promotions/promotions.helper.service';
import { useAllPromotionStoreGroups, useStoreGroupPromotions } from '@/services/promotions/promotions.service';
import { PromotionData } from '@/types/promotions.types';
import { CellContext, Row } from '@tanstack/react-table';
import { ChangeEvent, FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './Promotions.module.css';
import classNames from 'classnames';
import useFranchiseeAlias from '@/utils/useFranchiseeAlias/useFranchiseeAlias';
import Dropdown from '@/components/Dropdown/Dropdown';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { Alert, Grid } from '@mui/material';

const ViewDetails = ({ row }: CellContext<PromotionData, unknown>) => (
  <Link to={`/promotions/${row.original.promotionId}`}>
    <span className={styles.promotionName}>{row.original.name}</span>
  </Link>
);

const combinable = ({ row }: CellContext<PromotionData, unknown>) => (row.original.combinable ? 'Yes' : 'No');

const FILTER_BY_OPTIONS = [
  {
    label: 'All',
    value: '',
  },
  {
    label: 'Show CERs only',
    value: 'CER',
  },
  {
    label: 'Hide CERs',
    value: 'NON-CER',
  },
];

const Promotions: FC = () => {
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedFilter, setSelectedFilter] = useState(FILTER_BY_OPTIONS[0].value);
  const [registerCount, setRegisterCount] = useState(0);

  const { t } = useTranslation();
  const { data: sgPromos = [], isLoading, isError } = useStoreGroupPromotions(true, selectedGroup, selectedFilter);
  const { data: allStoreGroupData = [] } = useAllPromotionStoreGroups(true);
  const [showDropdown, setShowDropdown] = useState(true);

  const emptyStateText =
    selectedFilter === FILTER_BY_OPTIONS[1].value && sgPromos.length === 0 ? t('promotions.emptyStateMessage') : null;

  const promoStoreGroupOptions = useMemo(
    () =>
      allStoreGroupData?.map((group) => ({
        value: group,
        label: group,
      })),
    [allStoreGroupData],
  );

  useEffect(() => {
    if (promoStoreGroupOptions.length > 0) {
      setSelectedGroup(promoStoreGroupOptions[0].value);
    }
  }, [promoStoreGroupOptions]);

  useEffect(() => {
    if (isError) {
      showToast({
        label: t('error.fetchingPromotions'),
        toastType: ToastTypes.Error,
        id: 'errorFetchingPromotions',
      });
    }
  }, [t, isError]);

  const { alias } = useFranchiseeAlias();

  const handleRadioChange = (id: string) => {
    setSelectedFilter(FILTER_BY_OPTIONS[0].value);
    if (id === 'promoLibrary') {
      setShowDropdown(false);
      setSelectedGroup(' ');
    } else {
      setSelectedGroup(promoStoreGroupOptions[0]?.value);
      setShowDropdown(true);
    }
  };

  const handleDropdownSelect = (event: ChangeEvent<HTMLSelectElement>) => {
    setSelectedGroup(event.target.value);
  };

  const handleFilterByDropdown = (event: ChangeEvent<HTMLSelectElement>) => {
    setSelectedFilter(event.target.value);
  };

  return (
    <>
      <Header title={t('promotions.title')} breadcrumb={{ label: t('breadcrumb.backToHome'), to: '/' }} />
      <div className={styles.page}>
        <Card className={styles.radioCard}>
          <div className={styles.radioCardLabel}>
            <div>{t('promotions.show')}</div>
            <RadioGroup
              label=""
              options={[
                { label: t('promotions.myActivePromos'), id: 'myActivePromos' },
                { label: t('promotions.promoLibrary'), id: 'promoLibrary' },
              ]}
              selected={showDropdown ? 'myActivePromos' : 'promoLibrary'}
              onChange={(e) => handleRadioChange(e.target.id)}
            />
          </div>

          {showDropdown ? (
            <>
              <div className={styles.centerBorder} />
              <div className={styles.dropdownContainer}>
                <div className={styles.promoContainer}>
                  <div className={styles.centerBorderDropdown} />
                  <div className={styles.promoSgContainer}>
                    <Dropdown
                      className={styles.storeGroupDropdown}
                      label={t('promotions.storeGroupLabel')}
                      id="promoStoreGroups"
                      options={promoStoreGroupOptions}
                      value={selectedGroup}
                      onChange={(e) => handleDropdownSelect(e)}
                      hidePlaceholder
                      data-testid="promoDropdown"
                    />
                  </div>
                </div>
                <div className={styles.filterByDropdownContainer}>
                  <div className={styles.centerBorderDropdown} />
                  <div className={styles.filterByDropdown}>
                    <Dropdown
                      label={t('promotions.filterBy')}
                      id="promoFilterBy"
                      options={FILTER_BY_OPTIONS}
                      value={selectedFilter}
                      onChange={(e) => handleFilterByDropdown(e)}
                      hidePlaceholder
                      data-testid="promoFilterByDropdown"
                    />
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className={styles.centerBorder} />
              <div className={styles.dropdownContainer}>
                <div className={styles.filterByDropdownContainer}>
                  <div className={styles.centerBorderDropdown} />
                  <div className={styles.filterByDropdown}>
                    <Dropdown
                      label={t('promotions.filterBy')}
                      id="promoFilterBy"
                      options={FILTER_BY_OPTIONS}
                      value={selectedFilter}
                      onChange={(e) => handleFilterByDropdown(e)}
                      hidePlaceholder
                      data-testid="promoFilterByDropdown"
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </Card>

        {!emptyStateText && (
          <Grid
            container
            direction="row"
            justifyContent="right"
            sx={{
              marginBottom: '16px',
              fontSize: '14px',
              lineHeight: '25px',
              color: '#838383',
            }}
          >
            {t('promotions.promotionsLibraryCount', {
              count: registerCount,
            })}
          </Grid>
        )}

        {emptyStateText && (
          <Grid
            container
            direction="row"
            justifyContent="left"
            sx={{
              marginBottom: '16px',
              fontSize: '14px',
              lineHeight: '25px',
              color: '#131313',
            }}
          >
            {emptyStateText}
          </Grid>
        )}

        {!emptyStateText && (
          <Table
            width="calc(100vw - 325px)"
            height="calc(100vh - 170px)"
            isLoading={isLoading}
            setRegisterCount={setRegisterCount}
            data={sgPromos}
            className={classNames(styles.tableHeight, { [styles.withAlias]: alias !== null })}
            columns={[
              {
                accessorKey: 'name',
                header: t('promotions.table.name'),
                enableSorting: true,
                cell: ViewDetails,
              },
              {
                accessorKey: 'activeStoreCount',
                header: t('promotions.table.activeStoreCount'),
                enableSorting: true,
                filterFn: (row: Row<PromotionData>, columnId: string, filterValue) => {
                  const promotion = row.original;
                  return filterNumbers(promotion.activeStoreCount, filterValue);
                },
              },
              {
                accessorKey: 'occasions',
                header: t('promotions.table.occasion'),
                enableSorting: true,
                cell: DisplayOccasionWithoutIcon,
                filterFn: (row: Row<PromotionData>, columnId: string, filterValue) => {
                  const promotion = row.original;
                  return filterPromotionOccasions(promotion, filterValue);
                },
              },
              {
                accessorKey: 'combinable',
                header: t('promotions.table.combinable'),
                enableSorting: true,
                cell: combinable,
                filterFn: (row: Row<PromotionData>, columnId: string, filterValue) => {
                  const promotion = row.original;
                  return filterBooleans(promotion.combinable, filterValue);
                },
              },
              {
                accessorKey: 'maxUses',
                header: t('promotions.table.maxUses'),
                enableSorting: true,
                filterFn: (row: Row<PromotionData>, columnId: string, filterValue) => {
                  const promotion = row.original;
                  return filterNumbers(promotion.maxUses, filterValue);
                },
              },
            ]}
            enableFilters
          />
        )}
      </div>
    </>
  );
};

export default Promotions;
